export const agentRoles = {
  agent: 1,
  company_admin: 2,
  super_admin: 3,
  ai_agent: 5,
  // Job roles (multi-value, stored in agent_role_assignment)
  frontline: 10,
  backoffice: 11,
  management: 12,
  other: 13,
};

export const agentRoleLabels: Record<number, string> = {
  1: "Agent",
  2: "Corporate Admin",
  3: "Super Admin",
  5: "AI Agent",
  10: "Frontline",
  11: "Backoffice",
  12: "Management",
  13: "Other",
};

export const JOB_ROLES = [
  agentRoles.frontline,
  agentRoles.backoffice,
  agentRoles.management,
  agentRoles.other,
] as const;

export type JobRole = (typeof JOB_ROLES)[number];

export const isJobRole = (r: number): boolean =>
  (JOB_ROLES as readonly number[]).includes(r);

export const agentStatus = {
  online: 13,
  offline: 14,
};

export const agentStatusLabels: Record<number, string> = {
  13: "Online",
  14: "Offline",
};

// ---------------------------------------------------------------------------
// Role helpers — the single place that decides "who is an admin".
//
// Product rule: a Manager (job role `management`) has exactly the same rights
// as a company admin, in the UI and in the API. Every role gate in the app
// should go through these helpers rather than comparing agent_role directly.
// ---------------------------------------------------------------------------

type RoleUser =
  | { agent_role?: number | null; job_roles?: unknown }
  | null
  | undefined;

export const extractJobRoles = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r: any) => (typeof r === "object" && r !== null ? r.role : r))
    .filter((r: any): r is number => typeof r === "number");
};

export const isSuperAdminUser = (user: RoleUser): boolean =>
  user?.agent_role === agentRoles.super_admin;

/** Company admin *or* Manager (management job role). Never a super admin. */
export const isCompanyAdminUser = (user: RoleUser): boolean => {
  if (!user || isSuperAdminUser(user)) return false;
  if (user.agent_role === agentRoles.company_admin) return true;
  return extractJobRoles(user.job_roles).includes(agentRoles.management);
};

/** Company admin, Manager, or super admin. */
export const isAdminTierUser = (user: RoleUser): boolean =>
  isSuperAdminUser(user) || isCompanyAdminUser(user);
